import React, { useEffect, useState } from 'react';
import useApi from '../CustomHooks/useApi';
import { userAPI } from '../../api/apiClient';
import { useToast } from '../../Contexts/ToastContext';
import {
  FaUserShield,
  FaUser,
  FaUserSlash,
  FaUserPlus,
  FaSpinner,
  FaSortAlphaDown,
  FaStream
} from 'react-icons/fa';
import ConfirmWithPhrasePopup from "../Common/ConfirmWithPhrasePopup";
import Loading from '../../Loading';
import ActionIcon from '../Trips/Participants/ActionIcon';
import AddUserPopup from "./AddUserPopup";
import ManageRolesPopup from "./ManageRolesPopup";
import DeletedUsersList from "./DeletedUsersList";
import "../../css/Users/UsersManager.css";


const UsersManager = () => {
  const { callApi: fetchUsersApi, isLoading: isLoadingUsers } = useApi();
  const { callApi: deleteUserApi, isLoading: isDeletingUser } = useApi();
  const { callApi: reactivateUserApi, isLoading: isReactivatingUser } = useApi();
  const { showToast, clearToast } = useToast();

  const [users, setUsers] = useState([]);
  const [userToDelete, setUserToDelete] = useState(null);
  const [expandedUser, setExpandedUser] = useState(null);
  const [groupByRoles, setGroupByRoles] = useState(false);
  const [showAddUserPopup, setShowAddUserPopup] = useState(false);
  const [showManageRolesPopup, setShowManageRolesPopup] = useState(false);
  const [userToManageRoles, setUserToManageRoles] = useState(null);
  const [showDeletedUsers, setShowDeletedUsers] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const activeUsers = users.filter(u => u.isActive);
  const deletedUsers = users.filter(u => !u.isActive);

  const filteredUsers = activeUsers.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredDeletedUsers = deletedUsers.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    const data = await fetchUsersApi(() => userAPI.getUsers());
    if (data) {
      const sorted = [...data].sort((a, b) => a.name.localeCompare(b.name));
      setUsers(sorted);
    }
  };

  const groupUsers = (userList) => {
    const grouped = {};
    userList.forEach((user) => {
      user.roles.forEach((role) => {
        if (!grouped[role.name]) {
          grouped[role.name] = [];
        }
        grouped[role.name].push(user);
      });
    });
    return grouped;
  };

  const formatDate = (isoDate) => {
    const date = new Date(isoDate);
    return `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}.${date.getFullYear()}`;
  };

  const handleDeleteUser = (userGuid) => {
    setUserToDelete(userGuid);
  };

  const confirmDeleteUser = async () => {
    const user = users.find(u => u.guid === userToDelete);
    if (!user) {
      setUserToDelete(null);
      return;
    }
    showToast(`Usuwanie użytkownix ${user.name}...`, 'processing');
    setUserToDelete(null);
    await deleteUserApi(
      () => userAPI.deleteUser(user.guid),
      async () => {
        showToast('Użytkownix zostałx usuniętx.', 'success');
        await fetchUsers();
      },
      async () => {
        clearToast();
      }
    );
  };

  const handleReactivateUser = async (userGuid) => {
    const user = users.find(u => u.guid === userGuid);
    if (!user) return;

    showToast(`Przywracanie użytkownix ${user.name}...`, 'processing');
    await reactivateUserApi(
      () => userAPI.reactivateUser(userGuid),
      async () => {
        showToast('Użytkownix zostałx przywrócony.', 'success');
        await fetchUsers();
      },
      async () => {
        clearToast();
      }
    );
  };

  const toggleExpanded = (userGuid) => {
    setExpandedUser(expandedUser === userGuid ? null : userGuid);
  };

  const handleManageRolesClick = (user) => {
    setUserToManageRoles(user);
    setShowManageRolesPopup(true);
  };



  const renderUserCard = (user) => (
    <div key={user.guid} className="usersmanager-user-card" onClick={() => toggleExpanded(user.guid)}>
      <div className="usersmanager-user-main">
        <span className="usersmanager-user-name">
          <FaUser style={{ marginRight: 6 }} />
          {user.name}
        </span>
        <div className="usersmanager-user-actions">
          <div className="usersmanager-action-icon" onClick={(e) => { e.stopPropagation(); handleManageRolesClick(user); }}>
            <FaUserShield />
          </div>
          <div className="usersmanager-action-icon" onClick={(e) => { e.stopPropagation(); handleDeleteUser(user.guid); }}>
            {isDeletingUser && userToDelete === user.guid ? <FaSpinner className="fa-spin" /> : <FaUserSlash />}
          </div>
        </div>
      </div>
      {expandedUser === user.guid && (
        <div className="usersmanager-user-expanded">
          <div className="usersmanager-user-email">{user.email}</div>
          <div className="usersmanager-user-created">Dodano: {formatDate(user.createdAt)}</div>
          <div className="usersmanager-user-roles-badges">
            {user.roles.sort((a, b) => a.name.localeCompare(b.name)).map(r => (
              <span key={r.role} className="usersmanager-role-badge">{r.name}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
  
  return (
    <div className="usersmanager-container">
      <div className="usersmanager-header">
        <h1 className="usersmanager-title">Osoby użytkownicze</h1>
        <div className="usersmanager-actions">
          <ActionIcon icon={<FaUserPlus />} label="Dodaj" onClick={() => setShowAddUserPopup(true)} disabled={showDeletedUsers} />
          <ActionIcon icon={groupByRoles ? <FaSortAlphaDown /> : <FaStream />} label={groupByRoles ? "Lista A-Z" : "Grupuj w role"} active={groupByRoles} disabled={showDeletedUsers} onClick={() => setGroupByRoles(!groupByRoles)} />
          <ActionIcon icon={showDeletedUsers ? <FaUser /> : <FaUserSlash />} label={showDeletedUsers ? "Osoby aktywne" : "Osoby usunięte"} active={showDeletedUsers} onClick={() => { setShowDeletedUsers(!showDeletedUsers); setGroupByRoles(false); }} />
        </div>
      </div>

      <div className="usersmanager-searchbar">
        <input
          type="text"
          placeholder="Szukaj osoby po imieniu lub e-mailu..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {isLoadingUsers ? (
        <Loading text="Ładowanie użytkowników..." />
      ) : showDeletedUsers ? (
        <DeletedUsersList
          deletedUsers={filteredDeletedUsers}
          onReactivate={handleReactivateUser}
          isReactivating={isReactivatingUser}
        />
      ) : (
        <div className="usersmanager-userlist">
          {!groupByRoles && <div className="activeusers-header">Aktywne osoby</div>}
          {groupByRoles ? (
            Object.entries(groupUsers(filteredUsers)).map(([roleName, usersInRole]) => (
              <div key={roleName} className="usersmanager-role-group">
                <div className="usersmanager-role-title">{roleName}</div>
                {usersInRole.map(renderUserCard)}
              </div>
            ))
          ) : (
            filteredUsers.map(renderUserCard)
          )}
        </div>
      )}

      {userToDelete && (
        <ConfirmWithPhrasePopup
          text={
            <>
              Czy na pewno chcesz usunąć użytkownix{" "}
              <span style={{ fontWeight: "bold" }}>
                <FaUser /> {users.find(u => u.guid === userToDelete)?.name}
              </span>{" "}
              ?
            </>
          }
          phrase="okoń"
          onConfirm={confirmDeleteUser}
          onCancel={() => setUserToDelete(null)}
        />
      )}

      {showAddUserPopup && (
        <AddUserPopup
          onClose={() => setShowAddUserPopup(false)}
          onUserAdded={fetchUsers}
        />
      )}

      {showManageRolesPopup && userToManageRoles && (
        <ManageRolesPopup
          user={userToManageRoles}
          onClose={() => setShowManageRolesPopup(false)}
          onRolesUpdated={fetchUsers}
        />
      )}
    </div>
  );
};

export default UsersManager;
